import React, { useCallback, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import pickBy from "lodash/pickBy";
import isEmpty from "lodash/isEmpty";
import Theme from "@adsk/alloy-react-theme";
import Button from "@adsk/alloy-react-button";
import TextInput from "@adsk/alloy-react-text-input";
import Textarea from "@adsk/alloy-react-textarea";
import Panel from "@adsk/alloy-react-panel";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import {
  useLocations,
  LocationPicker,
  LocationNode,
} from "@adsk/acc-locations-components";
import {
  IssueAttributes,
  IssueStatus,
  Issue,
  IssueCustomAttributeMapping,
  IssueFailureInfo,
} from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { CenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  bulkEditIssuesAction,
  maybeRemoveFromLogAfterBulkUnpublishAction,
} from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useIssueBulkEditValidationSchema } from "../utils/validation/entitySchemas";
import { StatusDropdown } from "@adsk/acc-issues-common/components/input/StatusDropdown";
import { TypeDropdown } from "@adsk/acc-issues-common/components/input/TypeDropdown";
import { RootCauseDropdown } from "@adsk/acc-issues-common/components/input/RootCauseDropdown";
import { DateField } from "@adsk/acc-issues-common/components/input/DateField";
import { WatcherDropdown } from "@adsk/acc-issues-common/components/input/WatcherDropdown";
import { WatcherFieldLabel } from "@adsk/acc-issues-common/components/display/WatcherFieldLabel";
import { AssigneeDropdown } from "@adsk/acc-issues-common/components/input/ProjectMemberDropdown/AssigneeDropdown";
import { CustomFieldWithFormikContext } from "../input-field-components/CustomFieldWithFormikContext";
import { sanitizeIssueAttributeMap } from "../utils/data/issueFieldSanitization";
import {
  useGetVisibleBulkEditCustomFields,
  pruneCustomFieldsOnEditedAttributes,
} from "./customFieldsBulkEditing";
import { PanelSubHeader } from "../utils/components/Panel";
import { DividerLine } from "../utils/components/DividerLine";
import {
  trackIssueBulkEditSuccess,
  trackIssueBulkEditFailure,
  trackIssueBulkEditPartialFailure,
  trackBulkEditUnpublishAction,
  trackBulkEditPublishAction,
} from "../utils/analytics/issueActionsTracking";
import { IssueBulkActionDropdown } from "./IssueBulkActionDropdown";
import { ButtonDivider } from "../utils/components/ButtonDivider";
import { useShouldWarnBeforeUnpublish } from "../hooks/useShouldWarnBeforeUnpublish";
import { PermissionedField, PermissionedFieldTypes } from "./PermissionedField";
import {
  BulkEditPermissions,
  getPermissionsForBulkEdit,
  useShouldIncludeMemberFn,
} from "./bulkEditPermissions";
import { ConditionalTooltip } from "@adsk/acc-issues-common/components/primitive/ConditionalTooltip";
import { IssueBulkActionPartialFailureModal } from "./IssueBulkActionPartialFailureModal";
import {
  CrownFilledIcon,
  CrownStrikethroughIcon,
} from "@adsk/alloy-react-icon";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useRequiredFieldsRestrictions } from "./requiredFieldsRestrictions";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { useWatcherPropName } from "../utils/hooks/watchers/useWatcherPropName";
import { WatcherObject } from "@adsk/acc-issues-types";
import { useGetWatchersValue } from "../utils/hooks/watchers/useGetWatchersValue";
import { useMaybeShowWatchersNotification } from "@adsk/acc-issues-common/hooks/notifications/useMaybeShowWatchersNotification";

const useBulkEditChangeHandler = ({
  issues,
  onSave,
  showBulkActionPartialFailureModal,
}: {
  issues: Issue[];
  showBulkActionPartialFailureModal: ({
    failureInfo,
  }: {
    failureInfo: IssueFailureInfo[];
  }) => void;
  onSave?: () => void;
}) => {
  const issueUids = issues.map(issue => issue.id);
  const { T } = useTranslations();
  const { showNotification } = useNotifications();
  const dispatch = useDispatch();
  const getVisibleBulkEditCustomFields = useGetVisibleBulkEditCustomFields();

  const handleBulkChange = useCallback(
    async (values: Partial<IssueFormAttributes>) => {
      const visibleCustomFields = getVisibleBulkEditCustomFields({
        selectedTypeId: values.issueTypeId,
        selectedIssues: issues,
      });
      const visibleFieldAttributeIds: Set<string> = new Set();
      visibleCustomFields.forEach(visibleField => {
        visibleFieldAttributeIds.add(visibleField.attributeDefinitionId);
      });
      // sanitize the attributes (trims + formats the shape of custom attribute data)
      let editedAttributes = sanitizeIssueAttributeMap(values);
      editedAttributes = pickBy(editedAttributes, value => {
        // false will never be a component field default, so we explicitly allow it,
        // but we never want to send the empty string, undefined, or null,
        // since those may be default values in the form
        return value || value === false;
      });
      // ensure that we are only changing the custom fields that
      // are visible, and that we aren't nullifying them
      editedAttributes = pruneCustomFieldsOnEditedAttributes({
        editedAttributes,
        visibleFieldAttributeIds,
      });

      if (!isEmpty(editedAttributes)) {
        await dispatch(
          bulkEditIssuesAction({
            issueUids,
            editedAttributes,
            onSuccess: () => {
              showNotification({
                status: NOTIFICATION_STATUSES.SUCCESS,
                message: T.translate("issueBulkEdit.success", {
                  count: issueUids.length,
                }),
              });
              onSave && onSave();
              trackIssueBulkEditSuccess({
                selectedCount: issueUids.length,
                changedFields: editedAttributes,
              });
              if (editedAttributes.published === false) {
                dispatch(
                  maybeRemoveFromLogAfterBulkUnpublishAction({ issueUids })
                );
                trackBulkEditUnpublishAction({
                  selectedCount: issueUids.length,
                });
              }
              if (editedAttributes.published === true) {
                dispatch(
                  maybeRemoveFromLogAfterBulkUnpublishAction({ issueUids })
                );
                trackBulkEditPublishAction({ selectedCount: issueUids.length });
              }
            },
            onPartialFailure: failureInfo => {
              showNotification({
                status: NOTIFICATION_STATUSES.WARNING,
                message: T.translate(
                  "issueBulkEdit.editPartialFailure.description",
                  {
                    failureCount: failureInfo.length,
                  }
                ),
              });
              if (editedAttributes.published === false) {
                dispatch(
                  maybeRemoveFromLogAfterBulkUnpublishAction({ issueUids })
                );
              }
              trackIssueBulkEditPartialFailure({
                successCount: issueUids.length - failureInfo.length,
                failureCount: failureInfo.length,
              });
              showBulkActionPartialFailureModal({ failureInfo });
            },
            onFailure: failureInfo => {
              const errorStatusCodes = new Set();
              failureInfo.forEach(failureDatum => {
                errorStatusCodes.add(failureDatum.statusCode);
              });
              showNotification({
                status: NOTIFICATION_STATUSES.ERROR,
                message: T.translate("issueBulkEdit.failure", {
                  errorStatusCodes: [...errorStatusCodes].join(", "),
                }),
              });
              trackIssueBulkEditFailure({
                selectedCount: issueUids.length,
              });
            },
          })
        );
      } else {
        showNotification({
          status: NOTIFICATION_STATUSES.INFO,
          message: T.translate("issueBulkEdit.noOp"),
        });
      }
    },
    [
      dispatch,
      issueUids,
      T,
      showNotification,
      onSave,
      showBulkActionPartialFailureModal,
      getVisibleBulkEditCustomFields,
      issues,
    ]
  );
  return handleBulkChange;
};

export interface BulkEditPanelProps {
  issues: Issue[];
  onSave?: () => void;
}

export const IssueBulkEditPanel = ({
  issues,
  // we expect most clients to want to close the panel on save
  onSave,
}: BulkEditPanelProps) => {
  // partialFailureModalData as null means do not show the modal
  const [partialFailureModalData, setPartialFailureModalData] =
    useState<IssueFailureInfo[] | null>(null);
  const watcherProp = useWatcherPropName();
  const bulkEditPermissions = getPermissionsForBulkEdit({
    issues,
    watcherProp,
  });
  const bulkEditValidationSchema = useIssueBulkEditValidationSchema();
  const handleBulkChange = useBulkEditChangeHandler({
    issues,
    onSave,
    showBulkActionPartialFailureModal: ({ failureInfo }) => {
      setPartialFailureModalData(failureInfo);
    },
  });

  return (
    <Formik
      validationSchema={bulkEditValidationSchema}
      initialValues={
        {
          /** by default everything is undefined, which is what we want */
        }
      }
      onSubmit={handleBulkChange}
    >
      <>
        <IssueBulkEditTopBar
          issues={issues}
          onBulkChange={handleBulkChange}
          permissions={bulkEditPermissions}
        />
        <IssueBulkEditForm issues={issues} permissions={bulkEditPermissions} />
        <IssueBulkActionPartialFailureModal
          actionType="edit"
          failureInfo={partialFailureModalData}
          onAcknowledgement={() => {
            setPartialFailureModalData(null);
            // we don't trigger the onSave callback until the modal is acknowledged,
            // because otherwise the modal will disappear when this compoennt gets removed by the pasrent
            onSave && onSave();
          }}
        />
      </>
    </Formik>
  );
};

export const IssueBulkEditTopBar = ({
  issues,
  permissions,
  onBulkChange,
}: {
  issues: Issue[];
  permissions: BulkEditPermissions;
  onBulkChange: (values: Partial<IssueFormAttributes>) => void;
}) => {
  const { isSubmitting, setSubmitting } = useFormikContext<IssueAttributes>();
  const { T } = useTranslations();
  const publishButtonLabel = T.translate("issueBulkEdit.bulkPublish", {
    selectedCount: issues.length,
  });
  const unpublishButtonLabel = T.translate("issueBulkEdit.bulkUnpublish", {
    selectedCount: issues.length,
  });
  const shouldWarn = useShouldWarnBeforeUnpublish(issues);
  const handleChangePublishStatus = useCallback(
    (newPublishStatus: boolean) => {
      setSubmitting(true);
      onBulkChange({
        published: newPublishStatus,
      });
    },
    [onBulkChange, setSubmitting]
  );
  const [
    isUnpublishConfirmationModalOpen,
    setIsUnpublishConfirmationModalOpen,
  ] = useState(false);
  const { permittedFieldsMap, canDeleteAllSelectedIssues } = permissions;

  // if the form is submitting via the "Save" button, it's going to close soon and there
  // will be a spinner, so we don't want to show the top bar at all
  if (isSubmitting) {
    return null;
  }

  return (
    <>
      <PanelSubHeader
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <ConditionalTooltip
          condition={!permittedFieldsMap.published}
          content={T.translate("noPermissions")}
        >
          <Button
            disabled={!permittedFieldsMap.published}
            style={
              permittedFieldsMap.published
                ? { color: Theme.colors.adskBlue500 }
                : {}
            }
            variant={Button.VARIANTS.TERTIARY}
            renderIcon={p => <CrownFilledIcon {...p} />}
            onClick={() => {
              handleChangePublishStatus(true);
            }}
          >
            {publishButtonLabel}
          </Button>
        </ConditionalTooltip>
        <ButtonDivider />
        <ConditionalTooltip
          condition={!permittedFieldsMap.published}
          content={T.translate("noPermissions")}
        >
          <Button
            disabled={!permittedFieldsMap.published}
            style={
              permittedFieldsMap.published
                ? { color: Theme.colors.adskBlue500 }
                : {}
            }
            variant={Button.VARIANTS.TERTIARY}
            renderIcon={p => <CrownStrikethroughIcon {...p} />}
            onClick={() => {
              if (shouldWarn) {
                setIsUnpublishConfirmationModalOpen(true);
              } else {
                handleChangePublishStatus(false);
              }
            }}
          >
            {unpublishButtonLabel}
          </Button>
        </ConditionalTooltip>
        <div style={{ marginLeft: "auto" }}>
          <IssueBulkActionDropdown
            hasDeletePermission={canDeleteAllSelectedIssues}
            issueUids={issues.map(issue => issue.id)}
            onBeginDeletion={() => {
              setSubmitting(true);
            }}
          />
        </div>
      </PanelSubHeader>
      <DividerLine extendPx={-16} />
      <ModalConfirmation
        style={{}}
        show={isUnpublishConfirmationModalOpen}
        title={
          T.translate("issueBulkEdit.unpublishConfirmModalTitle") as string
        }
        message={T.translate("issueBulkEdit.unpublishConfirmModalMessage", {
          selectedCount: issues.length,
        })}
        confirmationButtonLabel={T.translate("actions.unpublish") as string}
        onConfirm={() => {
          handleChangePublishStatus(false);
          setIsUnpublishConfirmationModalOpen(false);
        }}
        onClose={() => setIsUnpublishConfirmationModalOpen(false)}
      />
    </>
  );
};

export const IssueBulkEditForm = ({
  issues,
  permissions,
}: {
  issues: Issue[];
  permissions: BulkEditPermissions;
}) => {
  const { T } = useTranslations();
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    errors,
    isSubmitting,
    submitForm,
  } = useFormikContext<IssueAttributes>();
  const { isLoading: areLocationsLoading } = useLocations();
  const getVisibleCustomFields = useGetVisibleBulkEditCustomFields();
  // if a type is not selected, this will be the intersection of types of all selected issues
  const issueCustomFieldMappings: IssueCustomAttributeMapping[] =
    getVisibleCustomFields({
      selectedTypeId: values.issueTypeId,
      selectedIssues: issues,
    });
  const [requiredFieldAlertModal, setRequiredFieldAlertModal] =
    useState<"status" | "type" | undefined>();

  useRequiredFieldsRestrictions({
    selectedTypeId: values.issueTypeId,
    selectedStatus: values.status,
    selectedIssues: issues,
    onRestrictedTypeSelected: () => {
      setFieldValue("issueTypeId", undefined);
      setRequiredFieldAlertModal("type");
    },
    onRestrictedStatusSelected: () => {
      setFieldValue("status", undefined);
      setRequiredFieldAlertModal("status");
    },
  });

  const watcherPropName = useWatcherPropName();
  const { getWatchersValue } = useGetWatchersValue();
  const selectedWatcherIds =
    watcherPropName === "watchers"
      ? values[watcherPropName]
      : values[watcherPropName]?.map(x => x.id);

  const {
    permittedFieldsMap,
    permittedStatuses,
    canAssignAllToAllSelectedIssues,
  } = permissions;

  const shouldIncludeMemberFn = useShouldIncludeMemberFn({
    canAssignAllToAllSelectedIssues,
  });

  const { value: shouldHideEmptyRolesAndCompanies } = useFeatureFlag(
    FeatureFlag.HideEmptyRolesAndCompanies
  );

  const watcherProp = useWatcherPropName();
  const { maybeShowWatchersNotification } = useMaybeShowWatchersNotification();
  const onWatchersDropdownCloseHandler = useCallback(() => {
    if (watcherProp === "watchers") {
      return;
    }
    const watchers = values[watcherProp];
    if (!watchers) {
      return;
    }
    maybeShowWatchersNotification({ watchers });
  }, [maybeShowWatchersNotification, values, watcherProp]);

  return (
    <>
      <Panel.Body>
        {isSubmitting ? (
          <CenteredSpinner />
        ) : (
          <Form>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.title"),
                errorMessage: errors.title,
              }}
              type={PermissionedFieldTypes.Text}
              hasPermission={permittedFieldsMap.title}
            >
              <TextInput
                name="title"
                autoComplete="off"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={
                  T.translate("issueCreation.title.placeholder") as string
                }
                error={!!errors.title}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.status"),
              }}
              type={PermissionedFieldTypes.Dropdown}
              hasPermission={permittedFieldsMap.status}
            >
              <StatusDropdown
                // right now status is marked as optional in the sdk for issue -- is that actually true?
                // my understanding is that status is one of the few required fields...
                // for now, default to showing "undefined" as Open
                status={values.status}
                onChange={(newStatus: IssueStatus) => {
                  setFieldValue("status", newStatus);
                }}
                onClear={() => {
                  setFieldValue("status", undefined);
                }}
                defaultIsOpen={false}
                statusOptions={[...permittedStatuses]}
                isClearable
              />
            </PermissionedField>
            <PermissionedField
              type={PermissionedFieldTypes.Dropdown}
              fieldWrapperProps={{
                label: T.translate("issueAttributes.issueType"),
              }}
              hasPermission={permittedFieldsMap.issueTypeId}
            >
              <TypeDropdown
                selectedTypeId={values.issueTypeId || ""}
                onChange={typeId => {
                  setFieldValue("issueTypeId", typeId);
                }}
                issueStatus={values.status}
                onClear={() => {
                  setFieldValue("issueTypeId", undefined);
                }}
                isClearable
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.description"),
                errorMessage: errors.description,
              }}
              type={PermissionedFieldTypes.Text}
              hasPermission={permittedFieldsMap.description}
            >
              <Textarea
                style={{ width: "100%" }}
                // @ts-ignore https://jira.autodesk.com/browse/DSGN-661
                name="description"
                autoComplete="off"
                value={values.description}
                onChange={handleChange}
                maxHeight={70}
                minHeight={70}
                placeholder={
                  T.translate("issueCreation.description.placeholder") as string
                }
                error={!!errors.description}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.assignedTo"),
              }}
              type={PermissionedFieldTypes.Dropdown}
              hasPermission={permittedFieldsMap.assignedTo}
            >
              <AssigneeDropdown
                shouldIncludeMemberFn={shouldIncludeMemberFn}
                selectedAssigneeId={values.assignedTo}
                selectedWatchers={values.watcherObjects}
                onChange={({ assignedTo, assignedToType }) => {
                  setFieldValue("assignedTo", assignedTo);
                  setFieldValue("assignedToType", assignedToType);
                }}
                shouldIncludeEmpty={!shouldHideEmptyRolesAndCompanies}
                clearable={true}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: <WatcherFieldLabel />,
                testId: "watchers-field",
              }}
              type={PermissionedFieldTypes.Dropdown}
              hasPermission={permittedFieldsMap[watcherPropName]}
            >
              <WatcherDropdown
                shouldIncludeMemberFn={shouldIncludeMemberFn}
                selectedWatcherIds={selectedWatcherIds}
                onChange={(newSelectedWatcherObjects: WatcherObject[]) => {
                  setFieldValue(
                    watcherPropName,
                    getWatchersValue(newSelectedWatcherObjects)
                  );
                }}
                onClose={onWatchersDropdownCloseHandler}
                shouldIncludeEmpty={false}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.location"),
                loading: areLocationsLoading,
              }}
              type={PermissionedFieldTypes.Dropdown}
              hasPermission={permittedFieldsMap.locationId}
            >
              <LocationPicker
                hideIncludeSubLocations
                clearable
                onNodeSelect={location =>
                  setFieldValue(
                    "locationId",
                    location ? (location as LocationNode).id : null
                  )
                }
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.locationDetails"),
                errorMessage: errors.locationDetails,
              }}
              type={PermissionedFieldTypes.Text}
              hasPermission={permittedFieldsMap.locationDetails}
            >
              <TextInput
                name="locationDetails"
                autoComplete="off"
                value={values.locationDetails}
                onChange={handleChange}
                placeholder={
                  T.translate(
                    "issueCreation.locationDetails.placeholder"
                  ) as string
                }
                error={!!errors.locationDetails}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.dueDate"),
              }}
              type={PermissionedFieldTypes.Text}
              hasPermission={permittedFieldsMap.dueDate}
            >
              <DateField
                date={values.dueDate}
                onChange={newDate => {
                  setFieldValue("dueDate", newDate);
                }}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.startDate"),
              }}
              type={PermissionedFieldTypes.Text}
              hasPermission={permittedFieldsMap.startDate}
            >
              <DateField
                date={values.startDate}
                onChange={newDate => {
                  setFieldValue("startDate", newDate);
                }}
              />
            </PermissionedField>
            <PermissionedField
              fieldWrapperProps={{
                label: T.translate("issueAttributes.rootCause"),
              }}
              type={PermissionedFieldTypes.Dropdown}
              hasPermission={permittedFieldsMap.rootCauseId}
            >
              <RootCauseDropdown
                selectedRootCauseId={values.rootCauseId}
                onChange={({ rootCauseId }) => {
                  setFieldValue("rootCauseId", rootCauseId);
                }}
              />
            </PermissionedField>
            {permittedFieldsMap.customAttributes &&
              issueCustomFieldMappings.map(mapping => {
                return (
                  <CustomFieldWithFormikContext
                    {...mapping}
                    key={mapping.attributeDefinitionId}
                  />
                );
              })}
          </Form>
        )}
      </Panel.Body>
      <div style={{ margin: 12 }}>
        <Button
          style={{ width: "100%" }}
          onClick={submitForm}
          variant={Button.VARIANTS.PRIMARY}
          disabled={isSubmitting}
        >
          {T.translate("issueBulkEdit.save")}
        </Button>
      </div>
      {requiredFieldAlertModal === "type" && (
        <ModalConfirmation
          style={{}}
          show
          noCancelButton
          size={"medium"}
          isConfirmationButtonAlert={false}
          confirmationButtonLabel={T.translate("issueBulkEdit.okay") as string}
          title={T.translate("issueBulkEdit.reqFieldTypeModalHeader")}
          message={T.translate("issueBulkEdit.reqFieldTypeModalContent")}
          onConfirm={() => {
            setRequiredFieldAlertModal(undefined);
          }}
          onClose={() => setRequiredFieldAlertModal(undefined)}
        />
      )}
      {requiredFieldAlertModal === "status" && (
        <ModalConfirmation
          style={{}}
          show
          noCancelButton
          size={"medium"}
          isConfirmationButtonAlert={false}
          confirmationButtonLabel={T.translate("issueBulkEdit.okay") as string}
          title={T.translate("issueBulkEdit.reqFieldStatusModalHeader")}
          message={T.translate("issueBulkEdit.reqFieldStatusModalContent")}
          onConfirm={() => {
            setRequiredFieldAlertModal(undefined);
          }}
          onClose={() => setRequiredFieldAlertModal(undefined)}
        />
      )}
    </>
  );
};
